import { useAuth0 } from "@auth0/auth0-react";
import orderBy from "lodash-es/orderBy";
import React, { createContext, useContext } from "react";
import { createSignalRContext } from "react-signalr/signalr";
import { environment } from "../config/environment";
import { ChasableDetailsModel, ChasablesHubModel, MentionModel, PersonalActionsModel } from "../models/ChasableModels";
import { useLocalStorage } from "../utils/localStorage";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { TenantContext } from "./TenantContextProvider";
import { useTaskpaneTelemetry } from "../telemetry/useTaskpaneTelemetry";

const ChasablesContext = createContext<{
  chasables:
    | (ChasablesHubModel & {
        organisationActions: ChasablesHubModel["actions"];
        myActions: ChasablesHubModel["actions"];
      })
    | null
    | undefined;
  mentions: MentionModel[];
  isLoading: boolean;
  /** null = all users; otherwise filter by this assignee id */
  assignedToUserId: string | null;
  assignToMe: boolean;
  assignedToMeDisabled: boolean;
  isAssignedToMe: (id: string) => boolean;
  isAssignedToUser: (actionId: string, userId: string) => boolean;
  getActionsForUser: (userId: string) => ChasablesHubModel["actions"];
  onAssignedToUserIdChange: (userId: string | null) => void;
  skipDays: (invoiceId: string, days: number) => Promise<void>;
  completeChasable: (invoiceId: string) => Promise<void>;
  updateChasables: () => Promise<void>;
}>({
  chasables: null,
  mentions: [],
  isLoading: true,
  assignedToUserId: null,
  assignToMe: false,
  assignedToMeDisabled: false,
  isAssignedToMe: () => false,
  isAssignedToUser: () => false,
  getActionsForUser: () => [],
  onAssignedToUserIdChange: () => {},
  skipDays: () => Promise.resolve(),
  completeChasable: () => Promise.resolve(),
  updateChasables: () => Promise.resolve(),
});

export const ChasableSignalRContext = createSignalRContext();

export async function reconnect() {
  await ChasableSignalRContext.connection?.stop();
  await ChasableSignalRContext.connection?.start();
}

const ChasablesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    trackReceivableSkipApplied,
    trackReceivableSkipFailed,
    trackReceivableCompleteSucceeded,
    trackReceivableCompleteFailed,
  } = useTaskpaneTelemetry();
  const { tenant } = useContext(TenantContext);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { currentUser } = useCurrentUser();
  const [chasables, setChasables] = React.useState<ChasablesHubModel | null | undefined>(null);
  const [mentions, setMentions] = React.useState<MentionModel[]>([]);
  const [assignedToUserId, setAssignedToUserId] = React.useState<string | null>(null);
  const localStorage = useLocalStorage();
  const organisationActions = chasables?.actions ?? [];
  const orderedActions = orderBy(organisationActions, "type", "desc");
  const getActionsForUser = (userId: string) =>
    orderedActions.filter(({ assigedToId }) => !!assigedToId && assigedToId === userId);
  const myActions = currentUser?.id ? getActionsForUser(currentUser.id) : [];
  const assignedToMeDisabled = !myActions?.length;
  const assignToMe = !!currentUser?.id && assignedToUserId === currentUser.id;
  const filteredActions = assignedToUserId ? getActionsForUser(assignedToUserId) : orderedActions;

  const filteredChasables = chasables && {
    ...chasables,
    actions: filteredActions,
    organisationActions,
    myActions,
  };

  const onAssignedToUserIdChange = (userId: string | null) => {
    localStorage?.setItem(localStorage.keys.assignedToUserId, userId ?? "all");
    localStorage?.setItem(localStorage.keys.assignToMe, !!userId && userId === currentUser?.id);
    setAssignedToUserId(userId);
  };

  const isAssignedToUser = (actionId: string, userId: string) => {
    return getActionsForUser(userId).some((action) => action.id === actionId);
  };

  const isAssignedToMe = (id: string) => {
    return !!currentUser?.id && isAssignedToUser(id, currentUser.id);
  };

  React.useEffect(() => {
    if (!localStorage || !currentUser?.id) return;

    const storedAssignedTo = localStorage.getItem<string>(localStorage.keys.assignedToUserId);
    if (storedAssignedTo !== null) {
      setAssignedToUserId(storedAssignedTo === "all" ? null : storedAssignedTo);
      return;
    }

    // Migrate from legacy boolean assignToMe (default: me)
    const legacyAssignToMe = localStorage.getItem<boolean>(localStorage.keys.assignToMe);
    setAssignedToUserId(legacyAssignToMe === false ? null : currentUser.id);
  }, [localStorage, currentUser?.id]);

  const isLoading = ChasableSignalRContext.connection?.state !== "Connected";
  ChasableSignalRContext.useSignalREffect(
    "RecieveActions",
    (existingChasables: ChasablesHubModel) => {
      if (isAuthenticated) {
        setChasables(existingChasables);
      }
    },
    [isAuthenticated]
  );

  ChasableSignalRContext.useSignalREffect(
    "ReceivePersonalActions",
    (personalActions: PersonalActionsModel) => {
      if (isAuthenticated) {
        setMentions(personalActions.mentions);
      }
    },
    [isAuthenticated]
  );

  const skipDays = async (invoiceId: string, days: number) => {
    try {
      await ChasableSignalRContext.connection?.invoke<ChasableDetailsModel>("SkipExternal", invoiceId, {
        days,
      });
      await updateChasables();
      trackReceivableSkipApplied({ skip_days: String(days), invoice_count: "1" });
    } catch (e) {
      trackReceivableSkipFailed({
        skip_days: String(days),
        error: e instanceof Error ? e.message : String(e),
      });
      throw e;
    }
  };

  const completeChasable = async (invoiceId: string) => {
    try {
      await ChasableSignalRContext.connection?.invoke<ChasableDetailsModel>("CompleteExternal", invoiceId, {});
      await updateChasables();
      trackReceivableCompleteSucceeded({ invoice_count: "1" });
    } catch (e) {
      trackReceivableCompleteFailed({
        invoice_count: "1",
        error: e instanceof Error ? e.message : String(e),
      });
      throw e;
    }
  };

  const updateChasables = async () => {
    const newActions = await ChasableSignalRContext.connection?.invoke<ChasablesHubModel>("GetActions");
    setChasables(newActions);
  };

  const url = tenant
    ? `${environment.apiUrl}/hub/actions?Business-Tenant=${tenant}`
    : `${environment.apiUrl}/hub/actions`;

  const headers = tenant ? { "Business-Tenant": tenant } : undefined;

  return (
    <ChasableSignalRContext.Provider
      connectEnabled={isAuthenticated}
      accessTokenFactory={() => getAccessTokenSilently()}
      dependencies={[getAccessTokenSilently]}
      url={url}
      headers={headers}
    >
      <ChasablesContext.Provider
        value={{
          chasables: filteredChasables,
          mentions,
          isLoading,
          assignedToUserId,
          assignToMe,
          assignedToMeDisabled,
          isAssignedToMe,
          isAssignedToUser,
          getActionsForUser,
          onAssignedToUserIdChange,
          skipDays,
          completeChasable,
          updateChasables,
        }}
      >
        {children}
      </ChasablesContext.Provider>
    </ChasableSignalRContext.Provider>
  );
};

export { ChasablesContext, ChasablesProvider };
